import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { categoriesData } from "../../constant/categoriesData";
import { slugify } from "../../Common/slugify";
import {
  productImageUrl,
  productImageSrcSet,
  applyImageFallback,
} from "../../Common/productImage";
import SectionHeader from "./SectionHeader";

// A category tile is far easier to scan with a real product in it. Prefer a
// best seller, fall back to whatever the category's first item is.
const CATEGORIES = categoriesData.map((cat) => {
  const items = cat.subcategories || [];
  const hero = items.find((i) => i.bestSeller && i.image) || items[0];
  return {
    id: cat.id,
    name: cat.category,
    slug: slugify(cat.category),
    count: items.length,
    image: hero?.image,
  };
});

const FeaturedCategories = () => (
  // The banner's "Browse all categories" option scrolls here, rather than to
  // the merchandising block as a whole — that starts with the deals strip.
  <section id="shop-by-category" className="bg-white py-8">
    <div className="mx-auto max-w-[88rem] px-4 sm:px-6">
      <SectionHeader
        title="Shop IT hardware by category"
        subtitle="14 categories covering the full datacenter and workplace stack"
        to="/all-products"
        linkLabel="All products"
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7">
        {CATEGORIES.map((cat) => (
          <Link
            key={cat.id}
            to={`/category/${cat.slug}`}
            className="group flex flex-col items-center rounded-xl border border-gray-200 bg-white
                       p-3 text-center transition-all duration-200 hover:-translate-y-0.5
                       hover:border-gray-300 hover:shadow-[0_10px_24px_-16px_rgba(15,23,42,0.5)]"
          >
            {/* The tile holds the box; the image only has to fit inside it, so
                it keeps its own ratio rather than being stretched to the
                tile's. See the same note in Common/ProductCard.jsx. */}
            <div className="mb-2 flex h-20 w-full items-center justify-center rounded-lg
                            bg-[#f8fafc] p-2">
              <img
                src={productImageUrl(cat.image)}
                srcSet={productImageSrcSet(cat.image)}
                // Tracks the grid above: 2 columns on phones, then 3, 4 and
                // finally 7 — where each tile is only ~150px wide. These were
                // the worst offenders on the page, taking a 1000px file into a
                // 142x64 box.
                sizes="(max-width: 639px) 50vw, (max-width: 767px) 33vw, (max-width: 1023px) 25vw, 150px"
                alt=""
                loading="lazy"
                decoding="async"
                className="max-h-full max-w-full object-contain transition-transform duration-300
                           group-hover:scale-105"
                onError={applyImageFallback}
              />
            </div>

            <h3
              className="line-clamp-2 min-h-[2.2rem] text-[0.78rem] font-semibold leading-snug
                         text-gray-900 group-hover:text-[var(--website-primary-color)]"
            >
              {cat.name}
            </h3>
            <span className="mt-0.5 inline-flex items-center text-[0.68rem] text-gray-500">
              {cat.count} items
              <ChevronRight size={11} className="ml-0.5" />
            </span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export default FeaturedCategories;
